/**
 * Date-range presets for reports. Everything works on business dates (`YYYY-MM-DD`) in the shop's
 * time zone, so "today" is the shop's today, not the browser's.
 */

export type Preset = 'today' | '7d' | '30d' | 'thisMonth' | 'lastMonth' | 'thisYear' | 'custom';
export type Interval = 'day' | 'week' | 'month';

export interface DateRange {
  from: string;
  to: string;
}

export const PRESETS: { value: Exclude<Preset, 'custom'>; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
  { value: 'thisMonth', label: 'This month' },
  { value: 'lastMonth', label: 'Last month' },
  { value: 'thisYear', label: 'This year' },
];

export const INTERVALS: { value: Interval; label: string }[] = [
  { value: 'day', label: 'Daily' },
  { value: 'week', label: 'Weekly' },
  { value: 'month', label: 'Monthly' },
];

const ISO = /^\d{4}-\d{2}-\d{2}$/;

export function isIsoDate(value: unknown): value is string {
  if (typeof value !== 'string' || !ISO.test(value)) return false;
  const d = parse(value);
  return toIso(d) === value;
}

function parse(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1));
}

function toIso(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/** Adds (or subtracts) whole days to a business date. */
export function addDays(iso: string, days: number): string {
  const d = parse(iso);
  d.setUTCDate(d.getUTCDate() + days);
  return toIso(d);
}

/** Today's business date in a time zone, e.g. `2026-10-20` in Asia/Karachi. */
export function todayIn(timeZone: string, now: Date = new Date()): string {
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(now);
    const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
    return `${get('year')}-${get('month')}-${get('day')}`;
  } catch {
    return toIso(now);
  }
}

export function startOfMonth(iso: string): string {
  return `${iso.slice(0, 7)}-01`;
}

export function endOfMonth(iso: string): string {
  const d = parse(startOfMonth(iso));
  d.setUTCMonth(d.getUTCMonth() + 1);
  d.setUTCDate(0);
  return toIso(d);
}

/** The range a preset stands for, relative to `today`. */
export function presetRange(preset: Exclude<Preset, 'custom'>, today: string): DateRange {
  switch (preset) {
    case 'today':
      return { from: today, to: today };
    case '7d':
      return { from: addDays(today, -6), to: today };
    case '30d':
      return { from: addDays(today, -29), to: today };
    case 'thisMonth':
      return { from: startOfMonth(today), to: today };
    case 'lastMonth': {
      const lastDay = addDays(startOfMonth(today), -1);
      return { from: startOfMonth(lastDay), to: lastDay };
    }
    case 'thisYear':
      return { from: `${today.slice(0, 4)}-01-01`, to: today };
  }
}

/** Which preset (if any) a range matches; `custom` otherwise. */
export function detectPreset(range: DateRange, today: string): Preset {
  for (const p of PRESETS) {
    const r = presetRange(p.value, today);
    if (r.from === range.from && r.to === range.to) return p.value;
  }
  return 'custom';
}

/** Inclusive number of days in a range. */
export function daysIn(range: DateRange): number {
  return Math.round((parse(range.to).getTime() - parse(range.from).getTime()) / 86_400_000) + 1;
}

/** A sensible chart interval for a range length (about 7–60 points). */
export function suggestInterval(range: DateRange): Interval {
  const days = daysIn(range);
  if (days <= 62) return 'day';
  if (days <= 190) return 'week';
  return 'month';
}

/** Ensures from ≤ to (swaps when needed). */
export function normalizeRange(range: DateRange): DateRange {
  return range.from <= range.to ? range : { from: range.to, to: range.from };
}

/** The same-length range just before this one (for "vs previous period"). */
export function previousRange(range: DateRange): DateRange {
  const days = daysIn(range);
  return { from: addDays(range.from, -days), to: addDays(range.from, -1) };
}

/** Human label: "1 – 20 Oct 2026", "Sep 2026", "20 Oct 2026". */
export function describeRange(range: DateRange, locale = 'en'): string {
  const f = (iso: string, o: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat(locale, { ...o, timeZone: 'UTC' }).format(parse(iso));
  if (range.from === range.to)
    return f(range.from, { day: 'numeric', month: 'short', year: 'numeric' });
  if (range.from === startOfMonth(range.from) && range.to === endOfMonth(range.from))
    return f(range.from, { month: 'long', year: 'numeric' });
  const sameYear = range.from.slice(0, 4) === range.to.slice(0, 4);
  const sameMonth = sameYear && range.from.slice(0, 7) === range.to.slice(0, 7);
  const start = sameMonth
    ? f(range.from, { day: 'numeric' })
    : f(range.from, { day: 'numeric', month: 'short', ...(sameYear ? {} : { year: 'numeric' }) });
  return `${start} – ${f(range.to, { day: 'numeric', month: 'short', year: 'numeric' })}`;
}

/**
 * A period bucket as text. Short (chart axes): "1 Oct", "31 Aug", "Sep". Long (tables):
 * "Thu, 1 Oct 2026", "Week of 31 Aug 2026", "September 2026".
 */
export function periodLabel(
  interval: Interval,
  periodStart: string,
  locale = 'en',
  long = false,
): string {
  const f = (o: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat(locale, { ...o, timeZone: 'UTC' }).format(parse(periodStart));
  if (interval === 'month')
    return long ? f({ month: 'long', year: 'numeric' }) : f({ month: 'short', year: '2-digit' });
  if (interval === 'week')
    return long
      ? `Week of ${f({ day: 'numeric', month: 'short', year: 'numeric' })}`
      : f({ day: 'numeric', month: 'short' });
  return long
    ? f({ weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })
    : f({ day: 'numeric', month: 'short' });
}
